import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { toast } from '@/components/ui/toaster'
import { VisuallyHidden } from '@/components/ui/visually-hidden'
import { ConsentSettings } from '@/hooks/api/_types'
import { useConsentSettingsQuery } from '@/hooks/api/use-consent-settings-query'
import { useConsentSettingsUpdateMutation } from '@/hooks/api/use-consent-settings-update-mutation'
import { T, type TranslationKey, useTranslate } from '@tolgee/react'
import React from 'react'
import { Field, Form } from 'react-final-form'

export function ConsentDialog({ children }: Readonly<{ children: React.ReactNode }>) {
  const [open, setOpen] = React.useState(false)
  const { t } = useTranslate()
  const { data: savedConsent } = useConsentSettingsQuery()
  const updateConsentMutation = useConsentSettingsUpdateMutation({
    onSuccess: () => {
      toast.success(t('account.consent.toasts.success'))
      setOpen(false)
    },
    onError: (e) => {
      console.error(e)
      toast.error(t('account.consent.toasts.error'))
    },
  })
  const disabled = !savedConsent || updateConsentMutation.isPending
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogPortal>
        <DialogContent>
          <Form<ConsentSettings>
            onSubmit={(values) => updateConsentMutation.mutate(values)}
            initialValues={savedConsent ?? { allowMarketing: false, allowData: false }}
          >
            {({ handleSubmit, dirty }) => (
              <form className='flex flex-col gap-6' onSubmit={handleSubmit}>
                <DialogTitle className='text-2xl font-light'>
                  <T keyName='account.consent.dialog.title' />
                </DialogTitle>

                <VisuallyHidden asChild>
                  <DialogDescription>
                    <T keyName='account.consent.dialog.description' />
                  </DialogDescription>
                </VisuallyHidden>

                <Field name='allowData'>
                  {({ input }) => (
                    <SwitchFormItem
                      titleKey='account.consent.dialog.data.title'
                      descKey='account.consent.dialog.data.description'
                      ariaLabelKey='account.consent.dialog.data.a11y'
                      disabled={disabled}
                      {...input}
                    />
                  )}
                </Field>

                <div className='h-[1px] w-full bg-grey-100' />

                <Field name='allowMarketing'>
                  {({ input }) => (
                    <SwitchFormItem
                      titleKey='account.consent.dialog.marketing.title'
                      descKey='account.consent.dialog.marketing.description'
                      ariaLabelKey='account.consent.dialog.marketing.a11y'
                      disabled={disabled}
                      {...input}
                    />
                  )}
                </Field>

                <DialogFooter>
                  <DialogClose asChild>
                    <Button
                      disabled={updateConsentMutation.isPending}
                      variant='mango-outline'
                      title={t('shared.cancelDialog.a11y')}
                    >
                      <T keyName='shared.cancelDialog.title' />
                    </Button>
                  </DialogClose>
                  <Button
                    type='submit'
                    loading={updateConsentMutation.isPending}
                    title={t('account.consent.dialog.button.a11y')}
                    disabled={!dirty}
                  >
                    <T keyName='account.consent.dialog.button.title' />
                  </Button>
                </DialogFooter>
              </form>
            )}
          </Form>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

const SwitchFormItem = ({
  titleKey,
  descKey,
  ariaLabelKey,
  value,
  onChange,
  disabled,
  name,
}: {
  titleKey: TranslationKey
  descKey: TranslationKey
  ariaLabelKey: TranslationKey
  value?: boolean
  onChange: (value: boolean) => void
  disabled?: boolean
  name: string
}) => {
  const { t } = useTranslate()
  return (
    <div className='flex justify-between space-y-0'>
      <Label
        htmlFor={name}
        className='flex flex-col flex-wrap gap-3 font-circular-xx text-neutral-500'
      >
        <div className='text-xl font-light'>
          <T keyName={titleKey} />
        </div>
        <div className='leading-6'>
          <T keyName={descKey} />
        </div>
      </Label>
      <Switch
        name={name}
        className='shrink-0'
        aria-label={t(ariaLabelKey)}
        checked={value}
        onCheckedChange={onChange}
        disabled={disabled}
      />
    </div>
  )
}
